// Template model for labels and documents (spec §3.4 R3.8, plan "Label and document design
// tool", owner 2026-10-30: "a tool to design and change both labels and other documents such as
// delivery notes"). One model serves both: a page (label stock in mm, or a paper size) and a
// list of elements with a position in mm and a data binding. Labels are one page; documents
// flow — "page" elements repeat on every page (header, footer), "flow" elements stack in the
// body and a table may run over several pages.
//
// Every value here is settable in the editor (owner 2026-10-31: everything in admin). Nothing
// in this file knows about a printer language: the layout engine turns a template plus data
// into paint primitives, and the ZPL, PDF and SVG back ends draw those.
import { z } from "zod";
/** The document kinds Trail prints. Each has a typed data model (see `data.ts`) and a default
 *  template (see `defaults/`). `customer-label` is per customer and chosen by the customer rule. */
export const DocumentKind = z.enum([
    "pallet-label",
    "sscc-label",
    "piece-label",
    "item-label",
    "location-label",
    "customer-label",
    "delivery-note",
    "pick-list",
    "pallet-content",
    "count-sheet",
]);
/** Kinds printed on label stock through a label printer (ZPL); the rest are paper (PDF). */
export const LABEL_KINDS = ["pallet-label", "sscc-label", "piece-label", "item-label", "location-label", "customer-label"];
export const isLabelKind = (k) => LABEL_KINDS.includes(k);
const mm = z.number().finite();
const Expr = z.string().max(2000);
/** A text with `{{ expression | filter }}` interpolations (see `text.ts`). */
export const Interpolated = z.string().max(4000);
export const Rotation = z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]);
export const Font = z.object({
    /** `sans` is Helvetica / the printer's scalable font 0; `mono` is Courier / font 0 at fixed pitch. */
    family: z.enum(["sans", "mono"]).default("sans"),
    /** Size in points (1 pt = 0.3528 mm). */
    size: z.number().min(2).max(400).default(10),
    bold: z.boolean().default(false),
    italic: z.boolean().default(false),
});
const Base = {
    id: z.string().min(1).max(40),
    /** Shown in the element list; empty → a name from the type and binding. */
    name: z.string().max(80).default(""),
    x: mm,
    y: mm,
    w: mm.min(0),
    h: mm.min(0),
    rotation: Rotation.default(0),
    /** Boolean expression; the element is left out when it is false. Empty = always. */
    visibleWhen: Expr.default(""),
    locked: z.boolean().default(false),
    /** Documents: `page` elements repeat on every page at their position (y from the top, or from
     *  the bottom when `anchor` is `bottom`); `flow` elements stack in the body in list order. Labels
     *  ignore it. */
    band: z.enum(["page", "flow"]).default("page"),
    anchor: z.enum(["top", "bottom"]).default("top"),
};
export const TextElement = z.object({
    ...Base,
    type: z.literal("text"),
    text: Interpolated.default(""),
    font: Font.default({}),
    align: z.enum(["left", "center", "right"]).default("left"),
    valign: z.enum(["top", "middle", "bottom"]).default("top"),
    /** Wrap onto several lines inside the box. */
    wrap: z.boolean().default(false),
    /** `shrink`: the font shrinks until the text fits the box; `fill`: the glyphs are stretched
     *  both ways to fill the box (the pallet label's product number); `none`: clipped. */
    fit: z.enum(["none", "shrink", "fill"]).default("none"),
    /** Line height as a factor of the font size. */
    lineHeight: z.number().min(0.8).max(3).default(1.2),
    /** White text on a black box (ZPL ^FR; a filled rectangle behind in PDF/SVG). */
    inverse: z.boolean().default(false),
    /** Flow text (documents): `h` is the minimum; the box grows with the wrapped text. */
});
export const Symbology = z.enum(["gs1-128", "code128", "ean13", "ean8", "itf14", "code39", "qr", "datamatrix", "gs1-datamatrix", "gs1-qr"]);
export const BarcodeElement = z.object({
    ...Base,
    type: z.literal("barcode"),
    symbology: Symbology.default("code128"),
    /** The data; for GS1 symbologies an element string with the AIs in brackets:
     *  `(00){{ sscc }}` or `(01){{ gtin }}(10){{ lot }}`. FNC1 separators are inserted by the renderer. */
    value: Interpolated.default(""),
    /** Human-readable interpretation under (or over) the bars. For GS1 the AIs are kept in brackets. */
    hri: z.enum(["none", "below", "above"]).default("below"),
    hriFont: Font.default({ size: 9 }),
    /** Narrow bar (X dimension) in mm; 0 = fit the width of the box. GS1 logistic labels: 0.495–1.016. */
    moduleWidth: mm.min(0).max(3).default(0),
    /** Quiet zone the renderer keeps clear on each side, in mm (GS1: 10 × X minimum). */
    quietZone: mm.min(0).max(20).default(5),
});
export const LineElement = z.object({
    ...Base,
    type: z.literal("line"),
    thickness: mm.min(0.1).max(10).default(0.4),
});
export const BoxElement = z.object({
    ...Base,
    type: z.literal("box"),
    thickness: mm.min(0).max(10).default(0.4),
    fill: z.boolean().default(false),
    radius: mm.min(0).max(20).default(0),
});
export const ImageElement = z.object({
    ...Base,
    type: z.literal("image"),
    /** `company.logo` takes the company's logo from the settings; otherwise a PNG/JPEG data URL. */
    src: z.string().max(2_000_000).default("company.logo"),
    fit: z.enum(["contain", "stretch"]).default("contain"),
    /** Images on thermal labels are dithered to black and white; the threshold 0–255. */
    threshold: z.number().int().min(1).max(254).default(128),
});
export const TableColumn = z.object({
    id: z.string().min(1).max(40),
    header: Interpolated.default(""),
    /** Cell text, evaluated with `row` bound to the current row (and the document's data around it). */
    value: Interpolated.default(""),
    /** Width in mm; 0 = share what is left equally with the other 0 columns. */
    width: mm.min(0).default(0),
    align: z.enum(["left", "center", "right"]).default("left"),
});
export const TableElement = z.object({
    ...Base,
    type: z.literal("table"),
    /** Expression that gives the rows (a list), e.g. `lines`. */
    rows: Expr.default(""),
    columns: z.array(TableColumn).default([]),
    font: Font.default({ size: 9 }),
    headerFont: Font.default({ size: 8, bold: true }),
    /** Row height in mm; 0 = from the font. Wrapped cells grow the row. */
    rowHeight: mm.min(0).default(0),
    wrap: z.boolean().default(true),
    /** Repeat the header row at the top of every page the table runs onto. */
    repeatHeader: z.boolean().default(true),
    /** A second line under each row, e.g. the batches of a delivery-note line. Evaluated with `row`
     *  bound; empty = none. A list-valued expression gives one line per item (`sub` bound). */
    subRows: Expr.default(""),
    subRowText: Interpolated.default(""),
    /** A row is drawn only when this is truthy (`row` bound); empty = every row. */
    rowWhen: Expr.default(""),
    /** Grouping: rows with the same key (`row` bound) form a group in order of first appearance; a
     *  header line before it and a subtotal line after it, both with `group` bound
     *  ({ key, count, rows }) — e.g. `{{ group.rows | sum('delivered') | number }}`. */
    groupBy: Expr.default(""),
    groupText: Interpolated.default(""),
    subtotalText: Interpolated.default(""),
    rules: z.enum(["none", "rows", "grid"]).default("rows"),
    /** Shown instead of the table when the list is empty. */
    emptyText: Interpolated.default(""),
});
export const Element = z.discriminatedUnion("type", [TextElement, BarcodeElement, LineElement, BoxElement, ImageElement, TableElement]);
export const PageSetup = z.object({
    /** Width and height of the sheet or the label as the element coordinates see it, in mm. */
    width: mm.min(10).max(2000),
    height: mm.min(5).max(2000),
    /** Labels: print resolution of the target printer. */
    dpi: z.union([z.literal(203), z.literal(300), z.literal(600)]).default(203),
    /** Labels: the whole design is turned when the stock runs through the printer the other way
     *  (the landscape pallet label on 102 mm stock is designed 220 wide and printed rotated 90°). */
    printRotation: Rotation.default(0),
    /** Documents: printable margins in mm (page elements may ignore them). */
    margins: z.object({ top: mm.min(0), right: mm.min(0), bottom: mm.min(0), left: mm.min(0) }).default({ top: 15, right: 15, bottom: 15, left: 15 }),
    /** Labels: darkness and speed as the printer understands them; null = leave the printer's setting. */
    darkness: z.number().int().min(0).max(30).nullable().default(null),
    speed: z.number().int().min(1).max(14).nullable().default(null),
    /** Labels: gap or black-mark media; null = the printer's setting. */
    media: z.enum(["gap", "mark", "continuous"]).nullable().default(null),
});
export const Template = z.object({
    kind: DocumentKind,
    page: PageSetup,
    elements: z.array(Element).max(400).default([]),
    /** Editor state the renderer ignores. */
    grid: z.object({ step: mm.min(0.1).max(50).default(1), snap: z.boolean().default(true), show: z.boolean().default(true) }).default({}),
});
/** Common label stock in mm; the editor offers these and free entry. */
export const LABEL_STOCK = [
    { name: "102 × 152 (4 × 6 in)", width: 102, height: 152 },
    { name: "102 × 220 liggande", width: 220, height: 102 },
    { name: "102 × 76 (4 × 3 in)", width: 102, height: 76 },
    { name: "102 × 51 (4 × 2 in)", width: 102, height: 51 },
    { name: "57 × 32", width: 57, height: 32 },
    { name: "100 × 50", width: 100, height: 50 },
    { name: "A6 105 × 148", width: 105, height: 148 },
];
export const PAPER_SIZES = [
    { name: "A4 stående", width: 210, height: 297 },
    { name: "A4 liggande", width: 297, height: 210 },
    { name: "A5 stående", width: 148, height: 210 },
    { name: "Letter", width: 215.9, height: 279.4 },
];
export const PT_PER_MM = 72 / 25.4;
export const mmToPt = (v) => v * PT_PER_MM;
export const ptToMm = (v) => v / PT_PER_MM;
export const mmToDots = (v, dpi) => Math.round((v / 25.4) * dpi);
/** A fresh element of the given type at a position, with the defaults filled in. */
export function newElement(type, id, x, y, kindIsLabel) {
    const base = { id, x, y, band: kindIsLabel ? "page" : "flow" };
    switch (type) {
        case "text": return TextElement.parse({ ...base, type, w: 40, h: 8, text: "Text" });
        case "barcode": return BarcodeElement.parse({ ...base, type, w: 60, h: 20, value: "" });
        case "line": return LineElement.parse({ ...base, type, w: 60, h: 0 });
        case "box": return BoxElement.parse({ ...base, type, w: 40, h: 20 });
        case "image": return ImageElement.parse({ ...base, type, w: 40, h: 15 });
        // A table on a label repeats its rows where it stands (page band, clipped); on a document it flows.
        case "table": return TableElement.parse({ ...base, type, w: kindIsLabel ? 90 : 180, h: 20 });
    }
}
//# sourceMappingURL=model.js.map